import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import DropdownFilterForm from 'search/filters/base/DropdownFilterForm';
import {FilterKey, GenericFilter} from 'search/SearchConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pickDefined from 'util/pickDefined';

import {TemplateCollectionFilterFormQuery} from './__generated/TemplateCollectionFilterFormQuery';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

export interface TemplateCollectionFilterFormProps extends BaseFilterFormProps {
  filter: GenericFilter<FilterKey.TEMPLATE_COLLECTION>;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: GenericFilter<FilterKey.TEMPLATE_COLLECTION>) => void;
  onRemove?: () => void;
}

export const TEMPLATE_COLLECTION_FILTER_FORM_QUERY = gql`
  query TemplateCollectionFilterFormQuery(
    $searchInput: TemplateCollectionListingSearchInput
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    templateCollectionsListing(searchInput: $searchInput, limitOffsetInput: $limitOffsetInput) {
      collections {
        ... on TemplateCollectionNode {
          id
          name
        }
      }
    }
  }
`;

export const ALL_FILTER = {
  name: 'Any Template Collection',
  value: null,
};

function templateCollectionToOptionData(templateCollection: {id: string; name: string}): OptionData {
  return {
    name: templateCollection.name,
    value: templateCollection.id,
    icon: 'template',
  };
}

function getActiveTemplateCollectionFilterText(
  optionDatas: Array<OptionData>,
  filter: GenericFilter<FilterKey.TEMPLATE_COLLECTION>
): string {
  const filterOption = optionDatas.find((op) => op.value === filter.value);
  if (filter.value == null || filterOption == null) {
    return 'Template Collection';
  }

  return `Template Collection: ${filterOption.name}`;
}

export default function TemplateCollectionFilterForm(props: TemplateCollectionFilterFormProps): JSX.Element {
  const baseProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  let templateCollectionsData: TemplateCollectionFilterFormQuery['templateCollectionsListing']['collections'] =
    [];
  const result = useBenchlingQuery<TemplateCollectionFilterFormQuery>(TEMPLATE_COLLECTION_FILTER_FORM_QUERY);
  if (result.type === QueryResultType.SUCCESS) {
    templateCollectionsData = result.data.templateCollectionsListing.collections;
  }
  const optionDatas = [
    ALL_FILTER,
    ...templateCollectionsData.map((tc) => templateCollectionToOptionData(tc)),
  ];

  return (
    <DropdownFilterForm
      {...baseProps}
      dropdownToggleText={getActiveTemplateCollectionFilterText(optionDatas, props.filter)}
      filterStyle={props.filterStyle}
      isMulti={false}
      isSearchable={true}
      onChangeValue={(value: string) => {
        props.onChangeFilter({...props.filter, value});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      value={props.filter.value}
    />
  );
}
